<template>
  <div class="settings-container">
    <div class="header">
      <h2>NewebPay 金鑰設定</h2>
      <p class="subtitle">設定您的 NewebPay 金流參數</p>
    </div>

    <!-- 頁面層分頁切換：設定 vs. 已存卡片（R5 票 06）。故意不叫 activeTab——
         下面表單內已有一個同名狀態，用來切換測試／正式環境金鑰。 -->
    <div class="page-tab-group">
      <button type="button" :class="{active: currentSection==='config'}" @click="currentSection='config'">金鑰設定</button>
      <button type="button" :class="{active: currentSection==='cards'}" @click="currentSection='cards'">已存卡片</button>
    </div>

    <template v-if="currentSection==='cards'">
      <SavedCardsList :is-preview="isPreviewOrDevelopment" />
    </template>

    <template v-else>
    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>正在載入設定...</p>
    </div>

    <form v-else @submit.prevent="saveSettings" class="settings-form">
          <!-- 分頁切換：測試/正式 -->
          <div class="tab-group">
            <button type="button" :class="{active: activeTab==='test'}" @click="activeTab='test'">測試環境</button>
            <button type="button" :class="{active: activeTab==='live'}" @click="activeTab='live'">正式環境</button>
          </div>
          <!-- 測試環境設定 -->
          <div v-show="activeTab==='test'" class="env-block">
            <h3>測試環境金鑰</h3>
            <div class="form-group">
              <label for="test-merchantID">MerchantID (商店代號)</label>
              <input
                type="text"
                id="test-merchantID"
                v-model="testConfig.merchantId"
                :disabled="activeTab!=='test'"
                placeholder="請輸入測試商店代號"
                required
              >
            </div>
            <div class="form-group">
              <label for="test-hashKey">HashKey (雜湊金鑰)</label>
              <input
                type="text"
                id="test-hashKey"
                v-model="testConfig.hashKey"
                @focus="clearMaskOnFocus('test', 'hashKey')"
                :disabled="activeTab!=='test'"
                placeholder="請輸入測試 HashKey"
                required
              >
            </div>
            <div class="form-group">
              <label for="test-hashIV">HashIV (雜湊向量)</label>
              <input
                type="text"
                id="test-hashIV"
                v-model="testConfig.hashIv"
                @focus="clearMaskOnFocus('test', 'hashIv')"
                :disabled="activeTab!=='test'"
                placeholder="請輸入測試 HashIV"
                required
              >
            </div>
          </div>
          <!-- 正式環境設定 -->
          <div v-show="activeTab==='live'" class="env-block">
            <h3>正式環境金鑰</h3>
            <div class="form-group">
              <label for="live-merchantID">MerchantID (商店代號)</label>
              <input
                type="text"
                id="live-merchantID"
                v-model="liveConfig.merchantId"
                :disabled="activeTab!=='live'"
                placeholder="請輸入正式商店代號"
                required
              >
            </div>
            <div class="form-group">
              <label for="live-hashKey">HashKey (雜湊金鑰)</label>
              <input
                type="text"
                id="live-hashKey"
                v-model="liveConfig.hashKey"
                @focus="clearMaskOnFocus('live', 'hashKey')"
                :disabled="activeTab!=='live'"
                placeholder="請輸入正式 HashKey"
                required
              >
            </div>
            <div class="form-group">
              <label for="live-hashIV">HashIV (雜湊向量)</label>
              <input
                type="text"
                id="live-hashIV"
                v-model="liveConfig.hashIv"
                @focus="clearMaskOnFocus('live', 'hashIv')"
                :disabled="activeTab!=='live'"
                placeholder="請輸入正式 HashIV"
                required
              >
            </div>
          </div>
          <!-- 結帳頁網域（信任網域清單，跨測試/正式環境共用） -->
          <div class="env-block">
            <h3>結帳頁網域</h3>
            <div class="form-group">
              <label for="trusted-origins">已確認的結帳頁網域（多筆網域以逗號分隔）</label>
              <input
                type="text"
                id="trusted-origins"
                v-model="trustedOriginsInput"
                placeholder="例如 https://checkout.example.com, https://link.fastpaydirect.com"
              >
            </div>
          </div>
          <button type="submit" :disabled="saving" class="save-button">
            <span v-if="saving">儲存中...</span>
            <span v-else>儲存設定</span>
          </button>
        </form>
    
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>
    </template>
  </div>
</template>

<script>
import SavedCardsList from './SavedCardsList.vue';

/**
 * NewebPay 金鑰設定元件
 * 提供介面讓使用者輸入並儲存 NewebPay 的金鑰資訊與金流模式。
 */
export default {
  name: 'NewebPaySettings',
  components: {
    SavedCardsList,
  },
  data() {
    /**
     * 元件的資料模型
     * @returns {object} - 包含表單資料的物件
     * @property {string} mode - 金流模式 ('live' 或 'test')
     * @property {string} merchantID - NewebPay 商店代號
     * @property {string} hashKey - NewebPay HashKey
     * @property {string} hashIV - NewebPay HashIV
     */
    return {
      /**
       * 頁面層分頁（'config' 或 'cards'，R5 票 06）。與下面的 activeTab（測試／正式環境
       * 切換）是兩個獨立的狀態，故意不共用名稱。
       * @type {'config'|'cards'}
       */
      currentSection: 'config',
      /**
       * 當前分頁（test 或 live）
       * @type {string}
       */
      activeTab: 'test',
      /**
       * 測試環境金鑰設定
       * @type {{merchantId: string, hashKey: string, hashIv: string}}
       */
      testConfig: {
        merchantId: '',
        hashKey: '',
        hashIv: ''
      },
      /**
       * 正式環境金鑰設定
       * @type {{merchantId: string, hashKey: string, hashIv: string}}
       */
      liveConfig: {
        merchantId: '',
        hashKey: '',
        hashIv: ''
      },
      /**
       * 結帳頁信任網域，逗號分隔字串（對應輸入框），送出前拆解為陣列
       * @type {string}
       */
      trustedOriginsInput: '',
      /**
       * 原始設定狀態，用於判斷是否有修改
       * @type {{test: object, live: object, trustedOrigins: string[]}}
       */
      originalConfig: {
        test: null,
        live: null,
        trustedOrigins: []
      },
      loading: true,
      saving: false,
      message: '',
      messageType: 'success', // 'success' | 'error'
      userData: null
    };
  },
  computed: {
    isPreviewOrDevelopment() {
      return !!(this.userData && (this.userData.isPreview || this.userData.isDevelopment));
    },
  },
  async mounted() {
    console.log("Settings component mounted");
    await this.loadUserData();
    await this.loadSettings();
  },
  methods: {
    async loadUserData() {
      try {
        console.log("Loading user data in settings...");
        this.userData = await window.ghl.getUserData();
        console.log("Settings - user data:", this.userData);
      } catch (error) {
        console.error("Failed to load user data:", error);
        // 使用預設的模擬資料
        this.userData = {
          locationId: 'preview-location-id',
          isPreview: true
        };
        this.showMessage("預覽模式：使用示例資料", 'info');
      }
    },
    
    /**
     * 載入設定，將後端回傳的 test/live 金鑰填入對應區塊
     * @returns {Promise<void>}
     */
    async loadSettings() {
      try {
        if (!this.userData) {
          throw new Error("User data not loaded");
        }
        // 取得 locationId
        let locationId = this.userData.locationId;
        if (!locationId && this.userData.activeLocation) {
          locationId = this.userData.activeLocation;
          console.log("Using activeLocation as locationId:", locationId);
        }
        if (!locationId) {
          console.error("No locationId or activeLocation found in user data:", this.userData);
          throw new Error("No location ID available");
        }
        // 預覽模式：填入範例資料
        if (this.userData.isPreview || this.userData.isDevelopment) {
          this.testConfig = {
            merchantId: 'MS123456789',
            hashKey: '****測試金鑰****',
            hashIv: '****測試向量****'
          };
          this.liveConfig = {
            merchantId: 'MSLIVE987654321',
            hashKey: '****正式金鑰****',
            hashIv: '****正式向量****'
          };
          this.trustedOriginsInput = 'https://checkout.example.com';
          // 儲存原始狀態
          this.originalConfig.test = { ...this.testConfig };
          this.originalConfig.live = { ...this.liveConfig };
          this.originalConfig.trustedOrigins = [this.trustedOriginsInput];
          this.loading = false;
          return;
        }
        // 從 API 載入（結帳頁網域走獨立路由，載入失敗不影響金鑰設定顯示）
        try {
          const originsResponse = await fetch(`/api/newebpay/trusted-origins?locationId=${locationId}`);
          if (originsResponse.ok) {
            const originsResult = await originsResponse.json();
            const trustedOrigins = originsResult.trustedOrigins || [];
            this.trustedOriginsInput = trustedOrigins.join(', ');
            this.originalConfig.trustedOrigins = trustedOrigins;
          }
        } catch (originsError) {
          console.error("Failed to load trusted origins:", originsError);
        }
        const response = await fetch(`/api/newebpay/config?locationId=${locationId}`);
        if (response.ok) {
          const result = await response.json();
          console.log("Settings loaded from API:", result);
          // 預期後端回傳 { testConfig: {...}, liveConfig: {...} }
          const testConfig = result.config?.testConfig || { merchantId: '', hashKey: '', hashIv: '' };
          const liveConfig = result.config?.liveConfig || { merchantId: '', hashKey: '', hashIv: '' };
          
          // 如果有現有的設定，顯示遮罩而不是實際值
          this.testConfig = {
            merchantId: testConfig.merchantId,
            hashKey: testConfig.hashKey ? '********' : '',
            hashIv: testConfig.hashIv ? '********' : ''
          };
          this.liveConfig = {
            merchantId: liveConfig.merchantId,
            hashKey: liveConfig.hashKey ? '********' : '',
            hashIv: liveConfig.hashIv ? '********' : ''
          };
          
          // 儲存原始狀態（判斷是否有實際值）
          this.originalConfig.test = {
            merchantId: testConfig.merchantId,
            hasHashKey: !!testConfig.hashKey,
            hasHashIv: !!testConfig.hashIv
          };
          this.originalConfig.live = {
            merchantId: liveConfig.merchantId,
            hasHashKey: !!liveConfig.hashKey,
            hasHashIv: !!liveConfig.hashIv
          };
        }
      } catch (error) {
        console.error("Failed to load settings:", error);
        // 錯誤時清空
        this.testConfig = { merchantId: '', hashKey: '', hashIv: '' };
        this.liveConfig = { merchantId: '', hashKey: '', hashIv: '' };
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * 儲存設定
     * 透過 fetch API 將使用者輸入的 NewebPay 金鑰資訊與金流模式送至後端 API。
     * @returns {Promise<void>}
     */
    /**
     * 儲存設定，將 test/liveConfig 一併送出
     * @returns {Promise<void>}
     */
    async saveSettings() {
      if (!this.userData) {
        this.showMessage("無法取得使用者資料", 'error');
        return;
      }
      // 取得 locationId
      let locationId = this.userData.locationId;
      if (!locationId && this.userData.activeLocation) {
        locationId = this.userData.activeLocation;
      }
      if (!locationId) {
        this.showMessage("無法取得 Location ID", 'error');
        console.error("No locationId or activeLocation found:", this.userData);
        return;
      }
      // 預覽模式：模擬儲存
      if (this.userData.isPreview || this.userData.isDevelopment) {
        this.saving = true;
        setTimeout(() => {
          this.saving = false;
          this.showMessage('預覽模式：設定已模擬儲存！', 'success');
        }, 1000);
        return;
      }
      this.saving = true;
      this.message = '';
      try {
        // 準備要傳送的資料 - 只傳送有修改的部分
        const payload = {};

        // 檢查測試環境是否有修改
        const testChanged = this.isConfigChanged('test');
        if (testChanged) {
          payload.testConfig = { ...this.testConfig };
        }

        // 檢查正式環境是否有修改
        const liveChanged = this.isConfigChanged('live');
        if (liveChanged) {
          payload.liveConfig = { ...this.liveConfig };
        }

        // 檢查結帳頁網域是否有修改（獨立路由，不與金鑰設定共用 payload）
        const trustedOrigins = this.parseTrustedOriginsInput();
        const trustedOriginsChanged = this.isTrustedOriginsChanged(trustedOrigins);

        // 如果沒有任何修改，顯示訊息並返回
        if (!testChanged && !liveChanged && !trustedOriginsChanged) {
          this.showMessage('沒有任何修改需要儲存', 'info');
          this.saving = false;
          return;
        }

        const messages = [];
        let messageType = 'success';

        if (testChanged || liveChanged) {
          console.log('Saving payload:', payload);
          const response = await fetch('/api/newebpay/config', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-location-id': locationId,
            },
            body: JSON.stringify(payload),
          });
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || response.statusText);
          }
          const result = await response.json();
          messages.push(result.message || '設定已儲存');
          if (result.status === 'warning') {
            messageType = 'warning';
          }
        }

        if (trustedOriginsChanged) {
          const originsResponse = await fetch('/api/newebpay/trusted-origins', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-location-id': locationId,
            },
            body: JSON.stringify({ trustedOrigins }),
          });
          if (!originsResponse.ok) {
            const error = await originsResponse.json();
            throw new Error(error.message || originsResponse.statusText);
          }
          const originsResult = await originsResponse.json();
          this.originalConfig.trustedOrigins = originsResult.trustedOrigins || trustedOrigins;
          this.trustedOriginsInput = this.originalConfig.trustedOrigins.join(', ');
          messages.push('結帳頁網域已更新');
        }

        this.showMessage(messages.join('；'), messageType);
      } catch (err) {
        console.error("Save settings error:", err);
        this.showMessage('儲存失敗: ' + err.message, 'error');
      } finally {
        this.saving = false;
      }
    },
    
    /**
     * 當使用者聚焦到輸入框時，如果是遮罩則清除
     * @param {string} env - 'test' 或 'live'
     * @param {string} field - 'hashKey' 或 'hashIv'
     */
    clearMaskOnFocus(env, field) {
      const config = env === 'test' ? this.testConfig : this.liveConfig;
      if (config[field] === '********') {
        config[field] = '';
      }
    },
    
    /**
     * 檢查指定環境的配置是否有修改
     * @param {string} env - 'test' 或 'live'
     * @returns {boolean} 是否有修改
     */
    isConfigChanged(env) {
      const currentConfig = env === 'test' ? this.testConfig : this.liveConfig;
      const original = this.originalConfig[env];
      
      if (!original) {
        // 沒有原始配置，如果有任何非空值就算有修改
        return !!(currentConfig.merchantId || currentConfig.hashKey || currentConfig.hashIv);
      }
      
      // 檢查 merchantId 是否有修改
      if (currentConfig.merchantId !== original.merchantId) {
        return true;
      }
      
      // 檢查 hashKey 是否有修改（不是遮罩且不為空）
      if (currentConfig.hashKey && currentConfig.hashKey !== '********') {
        return true;
      }
      
      // 檢查 hashIv 是否有修改（不是遮罩且不為空）
      if (currentConfig.hashIv && currentConfig.hashIv !== '********') {
        return true;
      }
      
      return false;
    },

    /**
     * 將「結帳頁網域」輸入框的逗號分隔字串拆解為去重、去空白的網域陣列
     * @returns {string[]}
     */
    parseTrustedOriginsInput() {
      return Array.from(
        new Set(
          this.trustedOriginsInput
            .split(',')
            .map((origin) => origin.trim())
            .filter((origin) => origin.length > 0)
        )
      );
    },

    /**
     * 檢查結帳頁網域清單是否相較原始狀態有修改
     * @param {string[]} trustedOrigins - 目前輸入框拆解後的網域陣列
     * @returns {boolean} 是否有修改
     */
    isTrustedOriginsChanged(trustedOrigins) {
      const original = this.originalConfig.trustedOrigins || [];
      if (trustedOrigins.length !== original.length) {
        return true;
      }
      const sortedCurrent = [...trustedOrigins].sort();
      const sortedOriginal = [...original].sort();
      return sortedCurrent.some((origin, index) => origin !== sortedOriginal[index]);
    },

    showMessage(text, type = 'success') {
      this.message = text;
      this.messageType = type;
      
      // 3秒後清除訊息
      setTimeout(() => {
        this.message = '';
      }, 3000);
    }
  },
};
</script>

<style scoped>
.settings-container {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
}
.page-tab-group {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #e9ecef;
}
.page-tab-group button {
  padding: 0.75rem 1.25rem;
  background: transparent;
  border: none;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
  transition: color 0.2s, border-color 0.2s;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
}
.page-tab-group button.active {
  color: #007bff;
  border-bottom-color: #007bff;
}
.tab-group {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.tab-group button {
  flex: 1;
  padding: 0.75rem 0;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px 8px 0 0;
  font-weight: 600;
  color: #007bff;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  border-bottom: none;
}
.tab-group button.active {
  background: #fff;
  color: #2c3e50;
  border-bottom: 2px solid #007bff;
}
.env-block {
  border: 1px solid #e9ecef;
  border-radius: 0 0 8px 8px;
  padding: 1.5rem 1rem 1rem 1rem;
  margin-bottom: 1.5rem;
  background: #f9f9fb;
}
.env-block h3 {
  margin-top: 0;
  color: #007bff;
  font-size: 1.1rem;
  margin-bottom: 1rem;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
}

.header h2 {
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  font-size: 1.8rem;
  font-weight: 600;
}

.header .subtitle {
  color: #666;
  margin: 0;
  font-size: 0.9rem;
}

.loading {
  text-align: center;
  padding: 2rem;
}

.spinner {
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007bff;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.settings-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group label {
  font-weight: 600;
  color: #34495e;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.mode-label {
  margin-bottom: 0.75rem;
}

.radio-group {
  display: flex;
  gap: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

.radio-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: normal !important;
  cursor: pointer;
  margin-bottom: 0 !important;
}

.radio-option input[type="radio"] {
  display: none;
}

.radio-button {
  width: 18px;
  height: 18px;
  border: 2px solid #007bff;
  border-radius: 50%;
  position: relative;
  transition: all 0.2s;
}

.radio-option input[type="radio"]:checked + .radio-button {
  background-color: #007bff;
}

.radio-option input[type="radio"]:checked + .radio-button::after {
  content: '';
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

input[type="text"],
input[type="password"] {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

input[type="text"]:focus,
input[type="password"]:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.save-button {
  width: 100%;
  padding: 0.875rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  margin-top: 1rem;
}

.save-button:hover:not(:disabled) {
  background-color: #0056b3;
  transform: translateY(-1px);
}

.save-button:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  transform: none;
}

.message {
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  text-align: center;
  animation: fadeIn 0.3s ease-in;
}

.message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.message.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.message.warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 768px) {
  .settings-container {
    margin: 1rem;
    padding: 1.5rem;
  }
  
  .radio-group {
    flex-direction: column;
    gap: 1rem;
  }
}
</style>